/**
 * Visemas: las formas de boca del avatar.
 *
 * Un visema es la posicion visible de los labios para un grupo de sonidos. El
 * espanol se cubre bien con doce: no hace falta una forma por fonema porque
 * muchos sonidos se ven iguales desde fuera (p, b y m son el mismo visema).
 *
 * Las trayectorias estan dibujadas sobre el mismo sistema de coordenadas que el
 * resto de la cara (viewBox 0 0 400 440), con la boca centrada en (200, 258).
 * Para sustituir la ilustracion basta con reemplazar estas trayectorias.
 */

export type VisemeId =
  | 'REST' // silencio, boca cerrada
  | 'A' // a
  | 'E' // e
  | 'I' // i
  | 'O' // o
  | 'U' // u
  | 'MBP' // m, b, p, v
  | 'FV' // f
  | 'TD' // t, d, n, l, s, z, r
  | 'CH' // ch, ll, y, ñ
  | 'K' // k, g, j, x, qu
  | 'RR'; // rr

export interface VisemeShape {
  /** Contorno de los labios. */
  lip: string;
  /** Dientes superiores, si se ven. */
  teeth?: string;
  /** Lengua, si se ve. */
  tongue?: string;
  /** Apertura relativa 0..1. Modula el desplazamiento de la mandibula. */
  open: number;
}

const TEETH_UPPER = 'M178,253 Q200,250 222,253 L222,259 Q200,262 178,259 Z';
const TONGUE = 'M189,266 Q200,261 211,266 Q206,273 200,273 Q194,273 189,266 Z';

export const VISEMES: Record<VisemeId, VisemeShape> = {
  REST: {
    lip: 'M170,256 Q200,268 230,256 Q200,261 170,256 Z',
    open: 0,
  },
  A: {
    lip: 'M172,250 Q200,242 228,250 Q232,278 200,287 Q168,278 172,250 Z',
    teeth: TEETH_UPPER,
    tongue: 'M186,274 Q200,268 214,274 Q207,282 200,282 Q193,282 186,274 Z',
    open: 1,
  },
  E: {
    lip: 'M166,252 Q200,246 234,252 Q230,273 200,277 Q170,273 166,252 Z',
    teeth: TEETH_UPPER,
    open: 0.62,
  },
  I: {
    lip: 'M166,254 Q200,250 234,254 Q230,265 200,267 Q170,265 166,254 Z',
    teeth: TEETH_UPPER,
    open: 0.3,
  },
  O: {
    lip: 'M182,250 Q200,243 218,250 Q227,266 200,281 Q173,266 182,250 Z',
    open: 0.8,
  },
  U: {
    lip: 'M186,252 Q200,247 214,252 Q221,264 200,273 Q179,264 186,252 Z',
    open: 0.5,
  },
  MBP: {
    lip: 'M168,257 Q200,252 232,257 Q200,264 168,257 Z',
    open: 0,
  },
  FV: {
    lip: 'M170,254 Q200,250 230,254 Q226,267 200,269 Q174,267 170,254 Z',
    teeth: TEETH_UPPER,
    open: 0.22,
  },
  TD: {
    lip: 'M172,252 Q200,248 228,252 Q226,269 200,273 Q174,269 172,252 Z',
    teeth: TEETH_UPPER,
    tongue: TONGUE,
    open: 0.4,
  },
  CH: {
    lip: 'M180,252 Q200,247 220,252 Q227,266 200,277 Q173,266 180,252 Z',
    teeth: TEETH_UPPER,
    open: 0.55,
  },
  K: {
    lip: 'M174,253 Q200,249 226,253 Q224,270 200,275 Q176,270 174,253 Z',
    teeth: TEETH_UPPER,
    open: 0.45,
  },
  RR: {
    lip: 'M174,253 Q200,249 226,253 Q224,268 200,272 Q176,268 174,253 Z',
    teeth: TEETH_UPPER,
    tongue: TONGUE,
    open: 0.38,
  },
};

/** Un visema colocado en el tiempo, relativo al inicio del audio. */
export interface VisemeCue {
  /** Milisegundos desde que empieza a sonar la frase. */
  at: number;
  /** Duracion prevista en milisegundos. */
  duration: number;
  id: VisemeId;
}

/**
 * Tabla de dominancia para el avatar fotografico.
 *
 * Es distinta de VISEMES a proposito. VISEMES describe la FORMA de los labios
 * y sirve para la ilustracion; esta describe cuanto baja la MANDIBULA, que es
 * lo unico que el rig fotografico puede mover.
 *
 * Por eso /o/ y /u/ tienen valores bajos: redondean los labios pero apenas
 * abren la mandibula. Medido sobre desplazamiento real de mandibula, /o/ abre
 * MENOS que /e/, y /u/ es la vocal mas cerrada del espanol.
 *
 *   a = dominancia: con cuanta fuerza ese segmento reclama la mandibula.
 *       Las consonantes tienen dominancia baja porque en el habla real no
 *       mandan sobre la mandibula: la vocal vecina sigue mandando.
 *   T = apertura objetivo.
 *   R = redondeo objetivo: cuanto entran las comisuras y cuanto baja el
 *       labio superior. En el video de referencia la anchura de la boca cae
 *       al 40-60 % de la maxima en /o/ y /u/, con los dientes tapados.
 *
 * De este reparto sale sola la co-articulacion: el movimiento pasa a ser uno
 * por SILABA (~8 por segundo, que es la banda real) en lugar de uno por
 * FONEMA (~17 por segundo, por encima del limite en que una cara de verdad
 * ya no tiene energia de movimiento).
 */
export const JAW: Record<VisemeId, { a: number; T: number; R: number; W: number }> = {
  // Apertura medida en el video del asesor: hablando, la mandibula casi nunca
  // se cierra (solo el 6 % de los fotogramas). Las consonantes se dicen con
  // la boca entreabierta; solo /m b p/ sellan los labios, y poco tiempo.
  // W es la ESTRECHEZ de la boca (0 = sonrisa ancha, 1 = lo mas estrecha),
  // medida con puntos faciales: en reposo su boca mide 0,70 de la
  // interocular, hablando 0,80 de mediana, y en la sonrisa abierta 0,93.
  // La foto de origen es esa sonrisa, asi que el reposo es estrecho.
  //        dominancia  apertura  redondeo  estrechez
  A:    { a: 1.0, T: 0.95, R: 0.15, W: 0.45 },
  E:    { a: 1.0, T: 0.7,  R: 0.0,  W: 0.25 },
  O:    { a: 1.0, T: 0.62, R: 0.9,  W: 0.95 },
  I:    { a: 1.0, T: 0.4,  R: 0.0,  W: 0.2 },
  U:    { a: 1.0, T: 0.35, R: 1.0,  W: 1.0 },
  MBP:  { a: 2.0, T: -0.6, R: 0.35, W: 0.8 },
  FV:   { a: 1.2, T: 0.25, R: 0.1,  W: 0.75 },
  TD:   { a: 0.5, T: 0.38, R: 0.1,  W: 0.7 },
  CH:   { a: 0.5, T: 0.42, R: 0.55, W: 0.8 },
  K:    { a: 0.4, T: 0.4,  R: 0.1,  W: 0.7 },
  RR:   { a: 0.5, T: 0.38, R: 0.1,  W: 0.7 },
  REST: { a: 1.5, T: 0.0,  R: 0.0,  W: 0.95 },
};

/** Estrechez de la boca callada: la de su video en reposo. */
export const REST_NARROW = 0.95;
